import { Clock, CheckCircle, DollarSign, Home, ThumbsUp, ShieldCheck, ScanLine, Calendar, Users, Ruler, CalendarClock, Hammer } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/kitchen.js';



export default function kitchen() {

  const service = { // change this along with the metadata
    title: 'Kitchen Remodeling',
    image: '/hero/best-kitchen-remodeling-in-los-angeles.webp',
    description: 'Transform your kitchen with Modern Build. Stunning, functional designs with premium finishes. Free estimate: (323) 300-4130',
    link: '/services/kitchen-remodeling',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Kitchen Remodeling Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Kitchen Remodeling Experts | Free Estimate | Modern Build</title>
        <meta name="title" content="Kitchen Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="description" content="Transform your kitchen with Modern Build. Stunning, functional designs with premium finishes. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="home remodeling, kitchen remodeling, bathroom remodeling, adu convertion, garge convertion, roofing, energy-efficient services, Los Angeles, califronia" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/kitchen-remodeling" />

        {/* Open Graph Meta Tags for social sharing */}
        <meta property="og:title" content="Kitchen Remodeling Experts | Free Estimate | Modern Build" />
        <meta property="og:description" content="Transform your kitchen with Modern Build. Stunning, functional designs with premium finishes. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-kitchen-remodeling-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/kitchen-remodeling" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kitchen Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Transform your kitchen with Modern Build. Stunning, functional designs with premium finishes. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-kitchen-remodeling-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Kitchen Remodeling Services | Free Estimate | Modern Build',
                description: 'Upgrade your kitchen with custom remodeling services tailored to your style and space by Modern Build.',
                slug: '/services/kitchen-remodeling'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>
      <Header />
      <Hero category="kitchen" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="KITCHEN REMODELING SERVICES"
        title="Design. Build. Transform Your Kitchen."
        background="white"
        description="At Modern Build, we turn outdated kitchens into stunning, high-functioning spaces tailored to your lifestyle. Whether you want a modern open-concept layout, custom cabinetry, or luxury finishes — our expert designers and craftsmen bring your vision to life with precision and care."
        extraText="From initial planning to final walkthrough, we manage every step of the process to ensure your new kitchen is both beautiful and built to last. No cookie-cutter remodels here — only custom solutions designed just for you."
        phone="(323) 300-4130"
        image1="/split/complete-kitchen-remodeling-services-in-los-angeles-ca.webp"
        image2="/split/kitchen-remodeling-services-in-los-angeles-ca.webp"
      />

<Benefits
  title="What’s Included In Your Kitchen Remodel?"
  subtitle="We handle everything from design to final inspection."
  items={[
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Custom Cabinetry & Storage Solutions",
    },
    {
      icon: <ScanLine className="w-8 h-8 text-primary" />,
      title: "Premium Countertops – Quartz, Granite & More",
    },
    {
      icon: <Clock className="w-8 h-8 text-primary" />,
      title: "Smart Appliances & Modern Lighting",
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "Electrical & Plumbing Upgrades",
    },
    {
      icon: <Calendar className="w-8 h-8 text-primary" />,
      title: "Streamlined Project Scheduling",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "City Permits & Inspections Included",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Personalized Design Consultation",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Flexible Financing Options Available",
      href: "/financing",
    },
  ]}
/>

      <ContentBlock
        subtitle="FULL-SERVICE REMODELING"
        title="Custom Kitchens Built Around Your Needs"
        description="We believe your kitchen should be as unique as you are. That’s why we offer end-to-end remodeling services — from demolition and structural work to lighting, plumbing, and luxury finishes. Whether you're looking to entertain, cook like a pro, or create a family-friendly hub, we build kitchens that reflect your lifestyle."
        extraText="Our team handles every detail — permitting, material sourcing, layout optimization, and final inspection — so you can focus on enjoying your space."
        buttonText="Get a Free Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Process"
        subtitle="Step-by-step from concept to completion"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Consultation",
            description:
              "Every great project starts with a conversation. We meet to understand your vision, needs, and lifestyle, ensuring your goals guide the process.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Design & Materials",
            description:
              "Our designers help you select layouts, materials, and finishes that match your aesthetic and budget — blending beauty with functionality.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Planning & Timeline",
            description:
              "We finalize detailed plans and establish a clear, efficient timeline. Permits, prep work, and communication all handled by our team.",
          },
          {
            icon: <Hammer className="w-10 h-10 text-primary" />,
            title: "Build & Follow-Up",
            description:
              "Our skilled craftsmen bring your design to life. We manage every detail and complete a thorough quality check before your final walkthrough.",
          },
        ]}
      />

      <Split
        subtitle="TAILORED DESIGN EXPERIENCE"
        title="Built for You, Designed With You"
        background="white"
        description="Our design team collaborates closely with you to develop a layout and aesthetic that fits your taste and daily needs. Whether you prefer minimal modern lines or warm traditional finishes, we ensure your new kitchen reflects your unique personality and home style."
        extraText="We offer 3D renderings, material samples, and mood boards to help you visualize your new space before construction begins — so there are no surprises, just results."
        phone="(323) 300-4130"
        image1="/split/kitchen-remodeling-in-los-angeles-ca.webp"
        image2="/split/best-kitchen-remodeling-in-los-angeles-ca.webp"
        reverse={true}
      />

      <CTASection
        title="Remodel Now. Pay Later."
        subtitle="100% Financing Available with $0 Down*"
        buttonText="Get Pre-Approved Today"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />


      <FAQ category="kitchen"/>
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
